import _capitalize from 'hs-lodash/capitalize';
import _get from 'hs-lodash/get';
// @ts-expect-error JS file
import { DEAL_AMOUNT_CALCULATOR_PROPERTY } from 'crm_data/constants/PortalSettingsKeys';
import { STATUS_CODES } from '../../constants/Http';
import { ACTIONS, SCREENS } from '../../constants/Tracking';
import { CARDINALITY_TYPES } from '../../constants/associations';
import { propertyFieldTypes, propertyReferenceTypes } from '../../constants/properties';
import { ASSOCIATION_TYPE_IDS, CRM_OBJECT_TYPES, singularTypeToPlural } from '../../constants/types';
import captureException from '../../utils/captureException';
import { buildCreateCrmObjectAssociationsBody } from '../../utils/helpers';
import { calculateDealAmount, generateCreateLineItemsBatchBody, productToNewLineItem } from '../../utils/lineItems';
import { CREATE_CRM_OBJECT_FULFILLED, CREATE_CRM_OBJECT_PENDING, CREATE_CRM_OBJECT_REJECTED, SAVE_CURRENCIES_INFO, SAVE_NEW_LINE_ITEMS, SET_ASSOCIATION_PROPERTIES, SET_DEAL_AMOUNT_CALCULATION_PROPERTY, SET_DEAL_TOTAL_AMOUNT, SET_PROPERTIES } from './reducer';
export const getAssociationPropertiesDefinitions = ({
  dispatch,
  apiWrapper
}, associationProperties) => {
  apiWrapper.getCreatorAssociationPropertiesDefinitions(associationProperties[0].objectTypeId).then(data => {
    const associationPropertiesHash = {};
    const associationsPropertiesDefinitions = [];
    associationProperties.forEach(item => {
      const key = item.identifier.split('-').pop();
      associationPropertiesHash[key] = item;
    });
    data.forEach(definition => {
      const targetProperty = associationPropertiesHash[definition.id];
      if (targetProperty) {
        associationsPropertiesDefinitions.push(Object.assign({}, targetProperty, {
          definition
        }));
      }
    });
    dispatch({
      type: SET_ASSOCIATION_PROPERTIES,
      payload: associationsPropertiesDefinitions.filter(associationDefinition => {
        if (associationDefinition.definition.cardinality === CARDINALITY_TYPES.ONE_TO_ONE) {
          // if there is a one-to-many association with the same object type remove one-to-one association from the list of the association properties
          return !associationsPropertiesDefinitions.some(item => item.definition.cardinality === CARDINALITY_TYPES.ONE_TO_MANY && item.definition.toObjectTypeId === associationDefinition.definition.toObjectTypeId);
        }
        return true;
      }).sort((a, b) => a.displayOrder - b.displayOrder)
    });
  }).catch(error => {
    captureException(error);
  });
};
export const calculateProperties = ({
  dispatch,
  apiWrapper
}, payload) => {
  apiWrapper.calculateProperties(payload).then(response => {
    const newLineItems = payload.lines.map((line, index) => productToNewLineItem({
      product: line.product,
      userId: payload.userId,
      calculations: response[index],
      quantity: line.quantity,
      hasMulticurrencyActive: payload.hasMulticurrencyActive,
      currency: payload.currency,
      index,
      appName: payload.appName
    }));
    const dealTotalAmount = calculateDealAmount({
      lineItems: newLineItems,
      dealAmountPreference: payload.dealAmountPreference,
      currency: payload.currency,
      userId: payload.userId,
      appName: payload.appName
    });
    dispatch({
      type: SAVE_NEW_LINE_ITEMS,
      payload: newLineItems
    });
    dispatch({
      type: SET_DEAL_TOTAL_AMOUNT,
      payload: dealTotalAmount.total
    });
  }).catch(captureException);
};
export const getCurrencies = ({
  dispatch,
  apiWrapper
}) => {
  apiWrapper.getMultiCurrencies().then(response => {
    dispatch({
      type: SAVE_CURRENCIES_INFO,
      payload: response
    });
  }).catch(captureException);
};
export const fetchDealAmountSettings = ({
  dispatch,
  apiWrapper
}) => {
  apiWrapper.getDealAmountSettings().then(dealAmountPreference => {
    if (dealAmountPreference.settingName === DEAL_AMOUNT_CALCULATOR_PROPERTY && typeof dealAmountPreference.settingValue === 'string') {
      dispatch({
        type: SET_DEAL_AMOUNT_CALCULATION_PROPERTY,
        payload: dealAmountPreference.settingValue
      });
    }
  }).catch(error => {
    // endpoint 404s when deal amount preference is not set
    // by default deal amount preference is ACV in the CRM settings page
    if (error.status !== STATUS_CODES.NOT_FOUND) {
      captureException(error);
    } else {
      dispatch({
        type: SET_DEAL_AMOUNT_CALCULATION_PROPERTY,
        payload: ''
      });
    }
  });
};
export const getObjectCreatorProperties = ({
  dispatch,
  apiWrapper
}, {
  crmObjectType,
  callback,
  errorCallback
}) => {
  apiWrapper.fetchObjectCreationFields(crmObjectType).then(propertiesResponse => {
    const inputProperties = [];
    const associationProperties = [];
    propertiesResponse.forEach(item => {
      if (item.type === 'PROPERTY') {
        inputProperties.push(item);
      } else {
        associationProperties.push(item);
      }
    });
    const inputs = inputProperties.map(el => ({
      name: el.identifier
    }));
    if (associationProperties.length) {
      getAssociationPropertiesDefinitions({
        dispatch,
        apiWrapper
      }, associationProperties);
    }
    return apiWrapper.fetchPropertiesDetails({
      objectType: crmObjectType,
      data: {
        inputs,
        archived: false
      }
    }).then(detailedPropertiesResponse => {
      if (typeof callback === 'function') callback();
      const propertiesResponseKeysHash = {};
      propertiesResponse.forEach(el => {
        propertiesResponseKeysHash[el.identifier] = el;
      });
      const payload = detailedPropertiesResponse.results
      // filter properties response to avoid destructuring undefined
      .filter(property => propertiesResponseKeysHash[property.name]).map(property => {
        const {
          displayOrder,
          required
        } = propertiesResponseKeysHash[property.name];
        const referenceType = propertyReferenceTypes[property.name];
        const fieldType = property.name === 'email' ? propertyFieldTypes.email : property.fieldType === propertyFieldTypes.phonenumber ? 'text' : property.fieldType;
        return Object.assign({}, property, {
          fieldType,
          displayOrder,
          required,
          referenceType
        });
      }).sort((a, b) => a.displayOrder - b.displayOrder);
      dispatch({
        type: SET_PROPERTIES,
        payload
      });
    });
  }).catch(error => {
    if (typeof errorCallback === 'function') errorCallback();
    captureException(error);
  });
};
export const createCrmObject = ({
  dispatch,
  apiWrapper
}, {
  data,
  crmObjectAssociations,
  newLineItems,
  crmObjectType,
  usageTracker,
  successCallback,
  cb
}) => {
  dispatch({
    type: CREATE_CRM_OBJECT_PENDING
  });
  let associations;
  if (crmObjectAssociations) {
    associations = buildCreateCrmObjectAssociationsBody(crmObjectAssociations);
  }
  if (associations && associations.length) {
    usageTracker.trackUsage(Object.assign({
      screen: SCREENS[`associate${_capitalize(crmObjectType)}`],
      type: crmObjectType
    }, ACTIONS.associations.associateCrmObjectToCreatedCrmObject));
  }
  apiWrapper.createCrmObject({
    data: Object.assign({
      properties: Object.assign({}, data)
    }, associations && {
      associations
    }),
    objectType: singularTypeToPlural[crmObjectType]
  }).then(entity => {
    usageTracker.trackUsage(Object.assign({
      screen: SCREENS[`associate${_capitalize(crmObjectType)}`],
      type: crmObjectType
    }, ACTIONS.general.createCrmObject));

    // if new line items - create and associate with new deal
    if (newLineItems && newLineItems.length) {
      apiWrapper.createCrmObjectBatch({
        objectType: CRM_OBJECT_TYPES.line_items,
        data: generateCreateLineItemsBatchBody({
          newLineItems,
          toObjectId: entity.id,
          associationTypeId: ASSOCIATION_TYPE_IDS.LINE_ITEMS_TO_DEAL
        })
      }).catch(captureException);
      usageTracker.trackUsage(Object.assign({
        screen: SCREENS[`associate${_capitalize(crmObjectType)}`],
        type: crmObjectType
      }, ACTIONS.associations.createLineItems));
    }
    if (typeof successCallback === 'function') successCallback({
      entity,
      type: singularTypeToPlural[crmObjectType]
    });
    dispatch({
      type: CREATE_CRM_OBJECT_FULFILLED
    });
    if (typeof cb === 'function') cb();
  }).catch(err => {
    dispatch({
      type: CREATE_CRM_OBJECT_REJECTED,
      payload: _get(err, 'responseJSON.message', 'error during object creation')
    });
    captureException(err);
  });
};